// A linked-source refresh (re-pull a Moxfield / Archidekt / ManaBox deck) that
// could not be written on its own: the deck changed while the pull ran, or the
// write's response was lost. Keith's rule: a refresh never silently wins or
// loses against a local edit, so the pulled list is HELD here and the user
// picks ("Use the Moxfield version" / "Keep my changes").
//
// In memory only, per (scope, deck): a reload drops the held list, and since
// nothing was written the deck is exactly as the user left it (they can hit
// Refresh again). The deck detail and the Decks list both read it, so a deck
// the bulk refresh could not write shows "Needs your choice" on its tile and
// the same prompt when opened.
import { useSyncExternalStore } from 'react'
import type { PendingSourceWrite, RefreshBase } from '../hooks/useDeckImportQueue'
import type { SaveDeckCheckedFn, SavedDeck, SaveOutcome } from '../hooks/useSavedDecks'
import type { CompleteAnalysis } from '../api/types'
import { getEditorSaveController, peekEditorSaveController, type FlushResult } from '../components/decks/editor/editorSaveQueue'
import { parseDecklist } from './decklist'

/** 'stale': the deck moved; the user picks. 'uncertain': the write may or may
 *  not have landed; it is checked before anything else writes. */
export type RefreshChoiceKind = 'stale' | 'uncertain'

export interface RefreshChoice {
  scope: string
  deckId: string
  kind: RefreshChoiceKind
  /** The pulled, analyzed list (never re-fetched to apply it). */
  write: PendingSourceWrite
  /** The deck's source platform code, for the copy. */
  source: string | null
  /** A "Use the source version" that was itself refused: it changed again. */
  again?: true
}

// eslint-disable-next-line i18next/no-literal-string -- a map key, never shown
const key = (scope: string, deckId: string) => `${scope}\u0000${deckId}`
let snapshot: ReadonlyMap<string, RefreshChoice> = new Map()
const listeners = new Set<() => void>()

function publish(next: Map<string, RefreshChoice>) {
  snapshot = next
  for (const l of listeners) l()
}

export function setRefreshChoice(c: RefreshChoice): void {
  const next = new Map(snapshot)
  next.set(key(c.scope, c.deckId), c)
  publish(next)
}

export function clearRefreshChoice(scope: string, deckId: string): void {
  if (!snapshot.has(key(scope, deckId))) return
  const next = new Map(snapshot)
  next.delete(key(scope, deckId))
  publish(next)
}

export function getRefreshChoice(scope: string, deckId: string): RefreshChoice | undefined {
  return snapshot.get(key(scope, deckId))
}

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}
const getSnapshot = () => snapshot

/** Every held choice (re-renders on any change). Look one up with choiceIn. */
export function useRefreshChoices(): ReadonlyMap<string, RefreshChoice> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function choiceIn(map: ReadonlyMap<string, RefreshChoice>, scope: string, deckId: string): RefreshChoice | undefined {
  return map.get(key(scope, deckId))
}

/** Test seam. */
export function __resetRefreshChoices(): void {
  publish(new Map())
}

/**
 * Cards the source version adds and removes against `mine`, by name and
 * quantity (main deck and command zone; the parser's own rule).
 */
export function cardDelta(mine: string | null | undefined, theirs: string): { added: number; removed: number } {
  const count = (text: string) => {
    const m = new Map<string, number>()
    for (const c of parseDecklist(text)) {
      const k = c.name.toLowerCase()
      m.set(k, (m.get(k) ?? 0) + c.quantity)
    }
    return m
  }
  const a = count(mine ?? '')
  const b = count(theirs)
  let added = 0
  let removed = 0
  for (const [k, n] of b) added += Math.max(0, n - (a.get(k) ?? 0))
  for (const [k, n] of a) removed += Math.max(0, n - (b.get(k) ?? 0))
  return { added, removed }
}

/**
 * A lost write held for this deck (by its save queue) is resolved FIRST:
 * nothing newer is written while one is out. 'none': nothing was held;
 * 'settled': one was, and is settled now (the row may have moved: read the
 * base fresh); 'held': still unsettled, so the caller writes nothing.
 */
export async function settleHeldWrite(scope: string, deckId: string): Promise<'none' | 'settled' | 'held'> {
  return (await settleHeldWriteOutcome(scope, deckId)).state
}

/** settleHeldWrite, plus how the held write resolved (the queue's flush):
 *  'stale' / 'missing' = it did NOT land. */
export async function settleHeldWriteOutcome(scope: string, deckId: string): Promise<{ state: 'none' | 'settled' | 'held'; flush?: FlushResult }> {
  const ctl = peekEditorSaveController(scope, deckId)
  // eslint-disable-next-line i18next/no-literal-string -- outcome codes
  if (!ctl || !ctl.hasUnknownAttempt()) return { state: 'none' }
  let flush: FlushResult
  try {
    flush = await ctl.flush()
  } catch {
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code
    return { state: 'held' }
  }
  // eslint-disable-next-line i18next/no-literal-string -- outcome codes
  return { state: ctl.hasUnknownAttempt() ? 'held' : 'settled', flush }
}

/**
 * The deck's save queue holds an edit the refresh's base does not: text not
 * yet durable (an edit inside its debounce, a write out, a stopped edit), a
 * Discard's fence, a lost write, or a durable text that moved past the base. Its rev can still
 * match the base until that edit's write lands, so the compare-and-swap alone
 * would let the refresh win and leave the edit stale. Such a refresh is not
 * written: the caller holds it as the choice prompt.
 */
export function editorBlocksRefresh(scope: string, base: RefreshBase): boolean {
  const ctl = peekEditorSaveController(scope, base.deckId)
  if (!ctl) return false
  // A Discard's fence is out (or owed): it must consume the row's revision
  // before anything else writes, so the refresh waits behind it.
  // A linked refresh's OWN queued state (its held attempt, its pulled text)
  // is not editor work and never blocks (it is settled first, see
  // settleHeldWrite); a fence, an edit, a re-rate's write always does.
  if (ctl.isFencing() || ctl.editorWorkPending()) return true
  return (ctl.editorText() ?? null) !== base.decklist
}

/**
 * "Keep my changes": the deck's queue drops a refresh's own settled state
 * (never an edit of the user's, never a write that may still land).
 */
export function dropRefreshQueueState(scope: string, deckId: string): void {
  peekEditorSaveController(scope, deckId)?.dropRefreshState()
}

/** What a save queue needs to resolve a held write on its own (a flush). */
export interface JournalArgs {
  saveDeckChecked: SaveDeckCheckedFn
  applyPersistedDeck?: (deck: SavedDeck) => void
  analysis: () => CompleteAnalysis
  partner: () => string | null
}

/**
 * A refresh write journaled through the deck's save queue, like every other
 * full save made outside the editor (a re-rate, a re-analysis): marked
 * uncertain while it is out, waited for by sign-out, and a lost response held
 * there and resolved before anything newer.
 */
export function journalRefreshWrite(scope: string, argsFor?: (deckId: string) => JournalArgs) {
  return (base: RefreshBase, text: string, save: () => Promise<SaveOutcome>): Promise<SaveOutcome> => {
    // eslint-disable-next-line i18next/no-literal-string -- an outcome code
    if (editorBlocksRefresh(scope, base)) return Promise.resolve({ status: 'stale' })
    const ctl = getEditorSaveController({
      scope,
      deckId: base.deckId,
      storedDecklist: base.decklist,
      ...(typeof base.rev === 'number' ? { storedRev: base.rev } : {}),
      storedZone: { commander: base.commander, partner: base.partner },
    })
    return ctl.runExternal(text, save, argsFor?.(base.deckId), { refresh: true })
  }
}
